/**
 * SoloLessonVideo — in-browser NotebookLM-style player for SOLO lessons.
 *
 * Renders the two-host dialogue bundle produced by /solo_video.py:
 *   • Sequentially plays each turn's audio (streamed via /video/audio/{i}.mp3
 *     — real URL, NOT blob:base64:, so iOS Safari + Android Chrome both
 *     honor autoplay-in-sequence within the user-gesture chain)
 *   • Auto-switches slides in sync with each turn's `slide` index
 *   • Shows the current speaker (Coach Adam or Analyst Rachel) + caption
 *   • First-open flow: POSTs /generate then polls /status every 8s
 *   • Post-generation: bundle is cached forever on the backend
 *   • Missing-audio detection: clear error + Regenerate button instead of
 *     a silent player that never speaks
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Play, Pause, RotateCcw, Sparkles, AlertTriangle, Volume2, VolumeX } from 'lucide-react';
import api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SPEAKER_META = {
  coach: { label: 'Coach Adam', accent: '#FF5E00' },
  analyst: { label: 'Analyst Rachel', accent: '#C9A961' },
};

const audioUrl = (lessonId, turnIndex) => {
  // ?auth=<token> so the <audio> element can fetch without a JS header.
  const token = localStorage.getItem('academy_token') || '';
  return `${BACKEND_URL}/api/academy/solo/lesson/${encodeURIComponent(lessonId)}/video/audio/${turnIndex}.mp3?auth=${encodeURIComponent(token)}`;
};

const SoloLessonVideo = ({ lessonId }) => {
  const [bundle, setBundle] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | not_generated | generating | ready | failed
  const [error, setError] = useState(null);
  const [turnIndex, setTurnIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playError, setPlayError] = useState(null);
  const audioRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Fetch cached bundle on mount
  const load = useCallback(async () => {
    if (!lessonId) return;
    try {
      const r = await api.get(`/academy/solo/lesson/${encodeURIComponent(lessonId)}/video`, { headers: authHeaders() });
      setBundle(r.data);
      setStatus(r.data?.status || 'not_generated');
      setError(r.data?.error || null);
    } catch (e) {
      setStatus('failed');
      setError(e?.response?.data?.detail || 'Could not load video');
    }
  }, [lessonId]);
  useEffect(() => { load(); }, [load]);

  // Poll while generating
  useEffect(() => {
    if (status !== 'generating') {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return undefined;
    }
    pollTimerRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/academy/solo/lesson/${encodeURIComponent(lessonId)}/video/status`, { headers: authHeaders() });
        const s = r.data?.status;
        if (s === 'ready') {
          clearInterval(pollTimerRef.current);
          load();
        } else if (s === 'failed') {
          clearInterval(pollTimerRef.current);
          setStatus('failed');
          setError(r.data?.error || 'Generation failed');
        }
      } catch { /* transient */ }
    }, 8000);
    return () => clearInterval(pollTimerRef.current);
  }, [status, lessonId, load]);

  const turns = bundle?.turns || [];
  const currentTurn = turns[turnIndex];
  const currentSlideIdx = currentTurn?.slide ?? 0;
  const currentSlide = bundle?.slides?.[currentSlideIdx] || bundle?.slides?.[0];
  const speaker = SPEAKER_META[currentTurn?.speaker] || SPEAKER_META.coach;

  // Turns with no audio (voice-gen failed for that turn). Empty = all good.
  const missingAudioIndices = useMemo(
    () => turns.map((t, i) => (t?.has_audio === false ? i : -1)).filter((i) => i >= 0),
    [turns],
  );
  const anyAudioMissing = missingAudioIndices.length > 0;

  // Play the CURRENT turn from the stream URL. Because we set src imperatively
  // inside the click handler AND immediately call play(), the browser keeps us
  // inside the user-gesture chain and honors playback on iOS.
  const _playCurrent = () => {
    const el = audioRef.current;
    if (!el) return Promise.reject(new Error('no audio element'));
    if (turnIndex >= turns.length) return Promise.reject(new Error('past end'));
    if (currentTurn?.has_audio === false) {
      return Promise.reject(new Error('turn has no audio'));
    }
    const wantSrc = audioUrl(lessonId, turnIndex);
    if (el.src !== wantSrc) {
      el.src = wantSrc;
      el.load();
    }
    const p = el.play();
    return p && typeof p.then === 'function' ? p : Promise.resolve();
  };

  const play = () => {
    setPlayError(null);
    setPlaying(true);
    _playCurrent().catch((err) => {
      // iOS may reject the first play() until canplay fires — chain it.
      const el = audioRef.current;
      if (!el) return setPlaying(false);
      const retry = () => {
        el.removeEventListener('canplay', retry);
        el.play().catch((err2) => {
          setPlaying(false);
          setPlayError(err2?.message || 'Playback blocked — tap play again');
        });
      };
      el.addEventListener('canplay', retry, { once: true });
    });
  };
  const pause = () => {
    setPlaying(false);
    if (audioRef.current) audioRef.current.pause();
  };
  const restart = () => {
    setTurnIndex(0);
    setTimeout(play, 30);
  };

  const onEnded = () => {
    if (turnIndex + 1 < turns.length) {
      setTurnIndex((i) => i + 1);
    } else {
      setPlaying(false);
      setTurnIndex(0);
    }
  };

  // Autoplay next segment on turn change (already in a gesture chain from
  // the previous onEnded).
  useEffect(() => {
    if (!playing) return;
    _playCurrent().catch(() => {
      const el = audioRef.current;
      if (!el) return setPlaying(false);
      const retry = () => {
        el.removeEventListener('canplay', retry);
        el.play().catch(() => setPlaying(false));
      };
      el.addEventListener('canplay', retry, { once: true });
    });
  }, [turnIndex]);

  const kickGenerate = async () => {
    setStatus('generating');
    setError(null);
    try {
      await api.post(`/academy/solo/lesson/${encodeURIComponent(lessonId)}/video/generate`, {}, { headers: authHeaders() });
    } catch (e) {
      setStatus('failed');
      setError(e?.response?.data?.detail || 'Could not start generation');
    }
  };

  const kickRegenerate = async () => {
    setStatus('generating');
    setError(null);
    setPlayError(null);
    setBundle(null);
    setTurnIndex(0);
    try {
      await api.post(`/academy/solo/lesson/${encodeURIComponent(lessonId)}/video/regenerate`, {}, { headers: authHeaders() });
    } catch (e) {
      setStatus('failed');
      setError(e?.response?.data?.detail || 'Could not restart generation');
    }
  };

  // ── Render states ──────────────────────────────────────────────────────
  if (status === 'idle') {
    return (
      <div className="border-2 border-zinc-900 bg-zinc-950 p-4 flex items-center gap-3" data-testid="solo-video-idle">
        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
        <span className="text-xs text-zinc-500 tracking-widest uppercase">Checking for explainer video…</span>
      </div>
    );
  }

  if (status === 'not_generated') {
    return (
      <div className="border-2 border-orange-500/40 bg-gradient-to-b from-orange-500/10 to-transparent p-5" data-testid="solo-video-cta">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-none border-2 border-orange-500 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-1">Coach's Corner · Explainer Video</div>
            <div className="text-sm text-white font-black mb-1">Want Coach Adam &amp; Analyst Rachel to walk you through this lesson?</div>
            <p className="text-xs text-zinc-400 mb-4 leading-relaxed">
              A 2-minute two-host breakdown with sports-playbook slides.
              Generates once, then plays instantly every time you come back.
            </p>
            <button
              onClick={kickGenerate}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black font-black text-xs uppercase tracking-[0.2em] px-5 py-3"
              data-testid="solo-video-generate-btn"
            >
              <Sparkles className="w-3.5 h-3.5" /> Generate Explainer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'generating') {
    return (
      <div className="border-2 border-orange-500/40 bg-orange-500/5 p-5 flex items-center gap-4" data-testid="solo-video-generating">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">Coach B is Recording Your Explainer</div>
          <div className="text-xs text-zinc-400 mt-1">Two voices + six playbook slides. Usually 30–45 seconds. Hang tight — auto-refreshes when ready.</div>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="border-2 border-red-500/40 bg-red-500/5 p-5" data-testid="solo-video-failed">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-red-400 mb-1">Explainer Generation Hit a Snag</div>
            <div className="text-xs text-zinc-400 mb-3">{error || 'Something went wrong on the studio side.'}</div>
            <button
              onClick={kickRegenerate}
              className="inline-flex items-center gap-2 border-2 border-red-500 text-red-400 hover:bg-red-500/10 font-black text-xs uppercase tracking-[0.2em] px-4 py-2"
              data-testid="solo-video-retry-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ready
  if (!turns.length || !bundle?.slides?.length) {
    return null;
  }

  return (
    <div className="border-2 border-orange-500/40 bg-black overflow-hidden" data-testid="solo-video-player">
      {/* Slide viewer */}
      <div className="relative bg-[#0F2419] aspect-video w-full">
        {currentSlide?.image_base64 ? (
          <img
            src={`data:${currentSlide.mime_type || 'image/png'};base64,${currentSlide.image_base64}`}
            alt={currentSlide.headline || 'Slide'}
            className="w-full h-full object-contain"
            data-testid="solo-video-slide"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs uppercase tracking-widest">
            Slide unavailable
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 px-4 py-2 bg-gradient-to-t from-black/95 to-transparent">
          <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-[0.3em]">
            <span style={{ color: speaker.accent }}>▶ {speaker.label}</span>
            <span className="text-zinc-500">Turn {turnIndex + 1} / {turns.length}</span>
          </div>
        </div>
      </div>

      {/* Missing-audio banner */}
      {anyAudioMissing && (
        <div className="px-4 py-3 border-t border-red-900/50 bg-red-500/10 flex flex-wrap items-center gap-3" data-testid="solo-video-missing-audio">
          <VolumeX className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-[11px] text-red-200 font-bold flex-1">
            {missingAudioIndices.length === turns.length
              ? "Voice audio didn't generate for this explainer. Tap Regenerate to try again."
              : `${missingAudioIndices.length} of ${turns.length} turns are missing voice audio. Regenerate for a full replay.`}
          </span>
          <button
            onClick={kickRegenerate}
            className="inline-flex items-center gap-1.5 border-2 border-red-400 text-red-300 hover:bg-red-500/20 font-black text-[10px] uppercase tracking-[0.2em] px-3 py-1.5"
            data-testid="solo-video-regenerate-btn"
          >
            <RotateCcw className="w-3 h-3" /> Regenerate
          </button>
        </div>
      )}

      {/* Playback error banner (browser blocked play / network issue) */}
      {playError && (
        <div className="px-4 py-2 border-t border-amber-900/50 bg-amber-500/10 text-[11px] text-amber-200 font-bold" data-testid="solo-video-play-error">
          {playError} — tap ▶ to try again.
        </div>
      )}

      {/* Caption strip */}
      <div className="px-4 py-3 border-t border-zinc-900 bg-zinc-950">
        <div className="text-[10px] tracking-[0.4em] uppercase font-black mb-1" style={{ color: speaker.accent }}>
          {speaker.label}
        </div>
        <div className="text-sm text-zinc-200 leading-relaxed">{currentTurn?.text}</div>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 border-t border-zinc-900 bg-black flex items-center gap-3">
        {playing ? (
          <button
            onClick={pause}
            className="w-11 h-11 bg-orange-500 hover:bg-orange-400 text-black flex items-center justify-center"
            aria-label="Pause"
            data-testid="solo-video-pause-btn"
          >
            <Pause className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={play}
            disabled={currentTurn?.has_audio === false}
            className="w-11 h-11 bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-black flex items-center justify-center"
            aria-label="Play"
            data-testid="solo-video-play-btn"
          >
            <Play className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={restart}
          className="w-9 h-9 border-2 border-zinc-800 hover:border-orange-500 text-zinc-400 hover:text-orange-500 flex items-center justify-center"
          aria-label="Restart"
          data-testid="solo-video-restart-btn"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 h-1 bg-zinc-900 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-orange-500 transition-all duration-300"
            style={{ width: `${((turnIndex + 1) / turns.length) * 100}%` }}
          />
        </div>
        <Volume2 className="w-3.5 h-3.5 text-zinc-600" />
      </div>

      {/* Persistent audio element — src assigned imperatively inside the
          click handler so the browser stays in the user-gesture chain. */}
      <audio
        ref={audioRef}
        onEnded={onEnded}
        onError={() => {
          setPlaying(false);
          setPlayError('Audio failed to load. Try Regenerate.');
        }}
        preload="none"
        playsInline
        crossOrigin="anonymous"
      />
    </div>
  );
};

export default SoloLessonVideo;
